import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const HUB_ORIGIN = '50% 50%';
const PILL_OFFSET = { x: 48, y: 28 };

/**
 * @param {HTMLElement} pill
 */
const pillEntranceFrom = (pill) => {
	if (pill.classList.contains('solution__pill--tr')) {
		return { x: PILL_OFFSET.x, y: -PILL_OFFSET.y };
	}
	if (pill.classList.contains('solution__pill--br')) {
		return { x: PILL_OFFSET.x, y: PILL_OFFSET.y };
	}
	if (pill.classList.contains('solution__pill--bl')) {
		return { x: -PILL_OFFSET.x, y: PILL_OFFSET.y };
	}
	return { x: -PILL_OFFSET.x, y: -PILL_OFFSET.y };
};

/**
 * @param {HTMLElement} section
 */
const bindSection = (section) => {
	const visual = section.querySelector('.js-solution-visual');
	if (!visual) {
		return null;
	}

	const orbitFills = visual.querySelectorAll('.solution__orbit-fill');
	const orbits = visual.querySelectorAll('.solution__orbit');
	const hubGlow = visual.querySelector('.solution__hub-glow');
	const hub = visual.querySelector('.solution__hub');
	const hubLogo = visual.querySelector('.solution__hub-logo');
	const connector = visual.querySelector('.solution__rating-connector');
	const ratingCluster = visual.querySelector('.solution__rating-cluster');
	const pills = visual.querySelectorAll('.solution__pill');
	const introBits = section.querySelectorAll(
		'.solution__intro .text-eyebrow, .solution__intro .text-h2, .solution__body, .solution__cta',
	);

	const orbitTargets = [...orbitFills, ...orbits];

	gsap.set(orbitTargets, { transformOrigin: HUB_ORIGIN, scale: 0.72, autoAlpha: 0 });
	if (hubGlow) {
		gsap.set(hubGlow, { autoAlpha: 0 });
	}
	gsap.set(hub, { transformOrigin: '50% 50%', scale: 0.82, autoAlpha: 0 });
	if (hubLogo) {
		gsap.set(hubLogo, { y: 10, autoAlpha: 0 });
	}
	if (connector) {
		gsap.set(connector, {
			xPercent: -50,
			transformOrigin: 'top center',
			scaleY: 0,
			autoAlpha: 0,
		});
	}
	if (ratingCluster) {
		gsap.set(ratingCluster, { y: 18, autoAlpha: 0 });
	}
	gsap.set(pills, { autoAlpha: 0 });
	pills.forEach((pill) => {
		const from = pillEntranceFrom(pill);
		gsap.set(pill, { x: from.x, y: from.y, scale: 0.92 });
	});
	if (introBits.length) {
		gsap.set(introBits, { y: 22, autoAlpha: 0 });
	}

	let idleTimeline = null;

	const startGlowBreathe = () => {
		if (hubGlow) {
			gsap.set(hubGlow, { clearProps: 'transform,opacity,visibility' });
		}
		if (orbitFills.length) {
			gsap.set(orbitFills, { clearProps: 'transform,opacity,visibility' });
		}
		visual.classList.add('solution__visual--breathe');
	};

	const entrance = gsap.timeline({
		scrollTrigger: {
			trigger: section,
			start: 'top 78%',
			once: true,
		},
		defaults: { ease: 'power3.out' },
		onComplete: () => {
			startGlowBreathe();
			idleTimeline = gsap.timeline({ repeat: -1, yoyo: true, defaults: { ease: 'sine.inOut' } });
			idleTimeline.to(
				visual.querySelector('.solution__orbit--outer'),
				{ opacity: 0.62, duration: 3.4 },
				0,
			);
			idleTimeline.to(
				visual.querySelector('.solution__orbit--inner'),
				{ opacity: 0.72, duration: 3.4 },
				0,
			);
		},
	});

	if (introBits.length) {
		entrance.to(introBits, { y: 0, autoAlpha: 1, duration: 0.7, stagger: 0.09 }, 0);
	}

	if (hubGlow) {
		entrance.to(hubGlow, { autoAlpha: 1, duration: 0.85, ease: 'power2.out' }, 0.12);
	}

	entrance
		.to(
			orbitFills,
			{ scale: 1, autoAlpha: 1, duration: 1, stagger: 0.1, ease: 'power2.out' },
			0.2,
		)
		.to(
			visual.querySelector('.solution__orbit--outer'),
			{ scale: 1, autoAlpha: 1, duration: 0.95, ease: 'back.out(1.35)' },
			0.28,
		)
		.to(
			visual.querySelector('.solution__orbit--inner'),
			{ scale: 1, autoAlpha: 1, duration: 0.9, ease: 'back.out(1.5)' },
			0.42,
		)
		.to(hub, { scale: 1, autoAlpha: 1, duration: 0.75, ease: 'back.out(1.7)' }, 0.52);

	if (hubLogo) {
		entrance.to(hubLogo, { y: 0, autoAlpha: 1, duration: 0.55 }, 0.68);
	}

	if (connector) {
		entrance.to(
			connector,
			{ scaleY: 1, autoAlpha: 1, duration: 0.65, ease: 'power2.inOut' },
			0.62,
		);
	}

	if (ratingCluster) {
		entrance.to(ratingCluster, { y: 0, autoAlpha: 1, duration: 0.55, ease: 'back.out(1.4)' }, 0.78);
	}

	entrance.to(
		pills,
		{
			x: 0,
			y: 0,
			scale: 1,
			autoAlpha: 1,
			duration: 0.65,
			stagger: 0.1,
			ease: 'back.out(1.35)',
		},
		0.48,
	);

	requestAnimationFrame(() => {
		ScrollTrigger.refresh();
		if (entrance.progress() === 1) {
			startGlowBreathe();
		}
	});

	const breatheFallback = new IntersectionObserver(
		([entry]) => {
			if (!entry?.isIntersecting) {
				return;
			}
			window.setTimeout(() => {
				if (!visual.classList.contains('solution__visual--breathe')) {
					startGlowBreathe();
				}
			}, 1400);
			breatheFallback.disconnect();
		},
		{ threshold: 0.15 },
	);
	breatheFallback.observe(section);

	return () => {
		breatheFallback.disconnect();
		visual.classList.remove('solution__visual--breathe');
		entrance.scrollTrigger?.kill();
		idleTimeline?.kill();
		gsap.killTweensOf([
			...orbitTargets,
			hubGlow,
			hub,
			hubLogo,
			connector,
			ratingCluster,
			...pills,
			...introBits,
		]);
		gsap.set(
			[...orbitTargets, hubGlow, hub, hubLogo, connector, ratingCluster, ...pills, ...introBits].filter(
				Boolean,
			),
			{ clearProps: 'all' },
		);
	};
};

/**
 * @param {HTMLElement} section
 */
const bindSectionReduced = (section) => {
	const visual = section.querySelector('.js-solution-visual');
	const introBits = section.querySelectorAll(
		'.solution__intro .text-eyebrow, .solution__intro .text-h2, .solution__body, .solution__cta',
	);
	if (!visual) {
		return null;
	}

	const targets = [visual, ...introBits];
	gsap.set(targets, { autoAlpha: 0 });

	const tween = gsap.to(targets, {
		autoAlpha: 1,
		duration: 0.55,
		stagger: 0.06,
		ease: 'power2.out',
		scrollTrigger: {
			trigger: section,
			start: 'top 85%',
			once: true,
		},
	});

	return () => {
		tween.scrollTrigger?.kill();
		gsap.killTweensOf(targets);
		gsap.set(targets, { clearProps: 'all' });
	};
};

export const initSolutionHubAnimation = () => {
	const sections = document.querySelectorAll('.section--solution');
	if (!sections.length) {
		return;
	}

	const cleanups = [];

	gsap.matchMedia().add('(prefers-reduced-motion: no-preference) and (min-width: 640px)', () => {
		sections.forEach((section) => {
			const cleanup = bindSection(section);
			if (cleanup) {
				cleanups.push(cleanup);
			}
		});

		return () => cleanups.forEach((fn) => fn());
	});

	gsap.matchMedia().add('(prefers-reduced-motion: no-preference) and (max-width: 639px)', () => {
		sections.forEach((section) => {
			const visual = section.querySelector('.js-solution-visual');
			if (visual) {
				gsap.set(visual.querySelectorAll('.solution__pill, .solution__hub, .solution__hub-logo'), {
					clearProps: 'transform,x,y,scale',
				});
			}
			const cleanup = bindSectionReduced(section);
			if (cleanup) {
				cleanups.push(cleanup);
			}
		});

		return () => cleanups.forEach((fn) => fn());
	});
};
